import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BaseResponse,
  BaseResponseSingle,
  IDiscountResponse,
} from "../../typings";
import {
  createDiscount,
  deleteDiscount,
  getDiscountsByStoreId,
} from "../actions/discountAction";

export interface DiscountState {
  createDiscountResponse: BaseResponseSingle<IDiscountResponse> | null;
  createDiscountLoading: boolean;
  createDiscountError: string | null;

  getDiscountByStoreResponse: BaseResponse<IDiscountResponse> | null;
  getDiscountByStoreLoading: boolean;
  getDiscountByStoreError: string | null;

  deleteDiscountResponse: BaseResponse<null> | null;
  deleteDiscountLoading: boolean;
  deleteDiscountError: string | null;

  selectedDiscount: string | null;

  eligibleDiscounts: IDiscountResponse | null;
  customDiscount: IDiscountResponse | null;

  // Set when the cashier explicitly removes the discount, so useCart's
  // auto-apply doesn't immediately re-apply the customer's default one.
  discountRemoved: boolean;

  // Editing an invoice: the discount it was created with can't be changed.
  // lockedDiscount is that discount (null = the invoice had none).
  discountLocked: boolean;
  lockedDiscount: IDiscountResponse | null;
  // Why it's locked, shown in the discount modal.
  lockedReason: string | null;
}

const initialState: DiscountState = {
  createDiscountResponse: null,
  createDiscountLoading: false,
  createDiscountError: null,

  getDiscountByStoreResponse: null,
  getDiscountByStoreLoading: false,
  getDiscountByStoreError: null,

  deleteDiscountResponse: null,
  deleteDiscountLoading: false,
  deleteDiscountError: null,

  selectedDiscount: null,
  eligibleDiscounts: null,
  customDiscount: null,
  discountRemoved: false,
  discountLocked: false,
  lockedDiscount: null,
  lockedReason: null,
};

const discountSlice = createSlice({
  name: "discount",
  initialState,
  reducers: {
    clearCreateDiscount: (state: DiscountState) => {
      state.createDiscountResponse = null;
      state.createDiscountError = null;
      state.createDiscountLoading = false;
    },
    clearGetDiscountByStore: (state: DiscountState) => {
      state.getDiscountByStoreResponse = null;
      state.getDiscountByStoreError = null;
      state.getDiscountByStoreLoading = false;
    },
    clearDeleteDiscount: (state: DiscountState) => {
      state.deleteDiscountResponse = null;
      state.deleteDiscountError = null;
      state.deleteDiscountLoading = false;
    },
    setSelectedDiscount: (state: DiscountState, action) => {
      state.selectedDiscount = action.payload;
      if (action.payload) state.discountRemoved = false;
    },
    setEligibleDiscount: (state: DiscountState, action) => {
      state.eligibleDiscounts = action.payload;
    },
    setCustomDiscount: (state: DiscountState, action) => {
      state.customDiscount = action.payload;
      if (action.payload) state.discountRemoved = false;
    },
    removeDiscount: (state: DiscountState) => {
      state.selectedDiscount = "none";
      state.eligibleDiscounts = null;
      state.customDiscount = null;
      state.discountRemoved = true;
    },
    lockDiscount: (
      state: DiscountState,
      action: PayloadAction<{ discount: IDiscountResponse | null; reason: string }>
    ) => {
      state.discountLocked = true;
      state.lockedDiscount = action.payload.discount;
      state.lockedReason = action.payload.reason;
    },
    resetDiscount: (state: DiscountState) => {
      state.selectedDiscount = null;
      state.eligibleDiscounts = null;
      state.customDiscount = null;
      state.discountRemoved = false;
      state.discountLocked = false;
      state.lockedDiscount = null;
      state.lockedReason = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createDiscount.rejected, (state: DiscountState) => {
      state.createDiscountResponse = null;
      state.createDiscountError = "Error creating discount";
      state.createDiscountLoading = false;
    });
    builder.addCase(createDiscount.pending, (state: DiscountState) => {
      state.createDiscountResponse = null;
      state.createDiscountError = null;
      state.createDiscountLoading = true;
    });
    builder.addCase(
      createDiscount.fulfilled,
      (state: DiscountState, action) => {
        state.createDiscountResponse = action.payload;
        state.createDiscountError = null;
        state.createDiscountLoading = false;
      }
    );

    builder.addCase(getDiscountsByStoreId.rejected, (state: DiscountState) => {
      state.getDiscountByStoreResponse = null;
      state.getDiscountByStoreError = "Error creating discount";
      state.getDiscountByStoreLoading = false;
    });
    builder.addCase(getDiscountsByStoreId.pending, (state: DiscountState) => {
      state.getDiscountByStoreResponse = null;
      state.getDiscountByStoreError = null;
      state.getDiscountByStoreLoading = true;
    });
    builder.addCase(
      getDiscountsByStoreId.fulfilled,
      (state: DiscountState, action) => {
        state.getDiscountByStoreResponse = action.payload;
        state.getDiscountByStoreError = null;
        state.getDiscountByStoreLoading = false;
      }
    );

    builder.addCase(deleteDiscount.rejected, (state: DiscountState) => {
      state.deleteDiscountResponse = null;
      state.deleteDiscountError = "Error creating discount";
      state.deleteDiscountLoading = false;
    });
    builder.addCase(deleteDiscount.pending, (state: DiscountState) => {
      state.deleteDiscountResponse = null;
      state.deleteDiscountError = null;
      state.deleteDiscountLoading = true;
    });
    builder.addCase(
      deleteDiscount.fulfilled,
      (state: DiscountState, action) => {
        state.deleteDiscountResponse = action.payload;
        state.deleteDiscountError = null;
        state.deleteDiscountLoading = false;
      }
    );
  },
});

export default discountSlice.reducer;
export const DiscountSliceAction = discountSlice.actions;
